import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "../../../../utils/axiosInstance";
import ServiceList from "./ServiceList";
import ServiceForm from "./ServiceForm";
import ConfirmModal from "../../../../components/Modals/ConfirmModal";
import BackButton from "../../../../components/Button/BackButton";
import SystemValuePicker from "../../../../components/SystemValue/SystemValuePicker";
import { SERVICE_CATALOG } from "../../../../utils/constants/systemValueCatalog";
import { bulkCreateSystemValues } from "../../../../utils/bulkCreateSystemValues";
import { toast } from "../../../../components/Toast/toast";
import { btn } from "../../../../components/Button/buttonStyles";

const ServicesPage = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [serviceToDelete, setServiceToDelete] = useState(null);
  const [adding, setAdding] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const action = searchParams.get("action");
  const editId = searchParams.get("id");

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      const res = await api.get("/admin-services");
      setServices(res.data.data);
    } catch (err) {
      console.error(err);
      setError("Failed to fetch services");
    } finally {
      setLoading(false);
    }
  };

  const setAction = (nextAction, id) => {
    const next = new URLSearchParams(searchParams);
    if (nextAction) next.set("action", nextAction);
    else next.delete("action");
    if (id !== undefined && id !== null) next.set("id", String(id));
    else next.delete("id");
    setSearchParams(next, { replace: true });
  };

  const handleSave = async (formData) => {
    try {
      const res = await api.put(`/admin-services/${editId}`, formData);
      setServices(
        services.map((s) => (String(s.id) === String(editId) ? res.data.data : s))
      );
      setAction(null);
    } catch (err) {
      console.error(err);
      toast.error("Failed to save service");
    }
  };

  const handleAdd = async (names) => {
    setAdding(true);
    const { created, failed } = await bulkCreateSystemValues("/admin-services", names);
    setAdding(false);

    if (created.length) setServices((prev) => [...prev, ...created]);
    if (failed.length) toast.error(`Could not add: ${failed.join(", ")}`);
    if (created.length) setAction(null);
  };

  const handleDelete = async () => {
    if (!serviceToDelete) return;
    try {
      await api.delete(`/admin-services/${serviceToDelete}`);
      setServices(services.filter((s) => s.id !== serviceToDelete));
      setServiceToDelete(null);
    } catch (err) {
      console.error(err);
      toast.error("Failed to delete service");
    }
  };

  if (loading)
    return <div className="text-center mt-10 text-gray-500">Loading...</div>;

  const inForm = action === "add" || (action === "edit" && editId);
  const editingService =
    action === "edit" && editId
      ? services.find((s) => String(s.id) === String(editId))
      : null;

  return (
    <>
      {/* The picker lays its catalog out in two columns, so it needs the room. */}
      <div className={action === "add" ? "md:w-2/3" : "md:w-1/2"}>
        <div className="flex justify-between items-center gap-3 mb-6">
          <div className="flex items-center gap-3">
            <BackButton
              onClick={inForm ? () => setAction(null) : undefined}
            />
            <h1 className="text-[22px] font-semibold tracking-tight text-gray-900">Services</h1>
          </div>
          {!inForm && (
            <button
              onClick={() => setAction("add")}
              className={btn("primary", "sm")}
            >
              Add Service
            </button>
          )}
        </div>

        {error && <p className="text-danger-600 text-sm mb-3">{error}</p>}

        {action === "add" ? (
          <SystemValuePicker
            catalog={SERVICE_CATALOG}
            existingNames={services.map((s) => s.name)}
            onAdd={handleAdd}
            onCancel={() => setAction(null)}
            label="service"
            labelPlural="services"
            addOtherLabel="Add Other Service"
            submitting={adding}
          />
        ) : inForm ? (
          <ServiceForm
            serviceToEdit={editingService}
            onSave={handleSave}
            onCancel={() => setAction(null)}
          />
        ) : (
          <ServiceList
            services={services}
            onEdit={(service) => setAction("edit", service.id)}
            onDelete={(id) => setServiceToDelete(id)}
          />
        )}
      </div>

      {serviceToDelete && (
        <ConfirmModal
          title="Confirm Deletion"
          message="Are you sure you want to delete this service? This action cannot be undone."
          onCancel={() => setServiceToDelete(null)}
          onConfirm={handleDelete}
          confirmText="Delete"
        />
      )}
    </>
  );
};

export default ServicesPage;
